import { api } from '@astro/api'
import { env } from '@astro/config'

/* ─────────────────────────────────────────────────────────────
   Puja Razorpay checkout driver — lifted from the shop app so the
   storefront checkout and the dashboard's Puja Bookings tab share
   one implementation (create-order response → popup → verify).
   ───────────────────────────────────────────────────────────── */

/** Readable message from an ApiRequestError: first Laravel field error when
 * the 422 carries an `errors` map, else the envelope/network message. */
export const apiErrorMessage = (err, fallback = 'Something went wrong — try again.') => {
  const details = err?.details
  if (details && typeof details === 'object') {
    const first = Object.values(details).flat().find((m) => typeof m === 'string')
    if (first) return first
  }
  return err?.message || fallback
}

/** Load the Razorpay checkout SDK once, on demand (same approach as the
 * product gateway driver — kept separate because the puja verify contract
 * differs: POST /v1/verify-payment with puja_booking_id). */
let sdk = null
function loadSdk() {
  if (window.Razorpay) return Promise.resolve()
  if (!sdk) {
    sdk = new Promise((resolve, reject) => {
      const s = document.createElement('script')
      s.src = 'https://checkout.razorpay.com/v1/checkout.js'
      s.onload = resolve
      s.onerror = () => {
        sdk = null
        reject(new Error('Could not load the payment SDK — check your connection.'))
      }
      document.body.appendChild(s)
    })
  }
  return sdk
}

/** /payment-gateways rides a { status, data } envelope or a bare array. */
const unwrapGateways = (res) => (Array.isArray(res) ? res : res?.data ?? [])

/** The Razorpay public key id off a gateway row — verified live home is
 * credentials.key_id; the rest are known backend-version variants. */
const gatewayRowKey = (g) =>
  g?.credentials?.key_id || g?.credentials?.key || g?.key_id || g?.key ||
  g?.public_key || g?.api_key || g?.config?.key_id || g?.config?.key || null

/**
 * Razorpay public key for the popup. create-order doesn't return one, so
 * after checking the order fields and env, fall back to the same source the
 * product checkout uses: the razorpay row of GET /payment-gateways
 * (credentials.key_id).
 */
async function resolveRazorpayKey(order) {
  const direct = order?.razorpay_key || order?.key || env.VITE_RAZORPAY_KEY
  if (direct) return direct
  try {
    const rows = unwrapGateways(await api.payments.gateways())
    const rzp = rows.find(
      (g) => String(g?.gateway_name ?? g?.slug ?? g?.name ?? '').toLowerCase().includes('razorpay'),
    )
    return gatewayRowKey(rzp)
  } catch {
    return null
  }
}

/**
 * Open Razorpay for a puja booking's order (POST /create-order response)
 * and verify server-side on capture. Resolves once the popup closes.
 *
 * `order.amount` is already in paise straight from the API — hand it to
 * Razorpay untouched.
 */
export async function payForPujaBooking({ booking, order, primary, onVerifyStart, onSuccess, onFailure }) {
  const key = await resolveRazorpayKey(order)
  if (!key) {
    onFailure('Payment key missing — contact support.')
    return
  }
  if (!order?.razorpay_order_id) {
    onFailure('Razorpay order reference missing — contact support.')
    return
  }
  await loadSdk()
  return new Promise((resolve) => {
    const rzp = new window.Razorpay({
      key,
      amount: order.amount,
      currency: order.currency || 'INR',
      name: 'Astrologer Raju Maharaj',
      description: booking?.puja?.title || 'Pooja Booking',
      order_id: order.razorpay_order_id,
      prefill: {
        name: primary?.name || '',
        email: primary?.email || '',
        contact: primary?.mobile || '',
      },
      // Site theme token --color-primary (deep maroon).
      theme: { color: '#5a0a16' },
      modal: { ondismiss: () => resolve() },
      handler: async (response) => {
        onVerifyStart?.()
        try {
          const v = await api.puja.verifyPayment({
            puja_booking_id: booking.id,
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
          })
          if (v?.success) onSuccess(v.data)
          else onFailure(v?.message || 'Payment verification failed — you can retry the payment.')
        } catch (err) {
          // Signature mismatch comes back as a 422 with a readable message —
          // surface it and let the user retry from the same booking.
          onFailure(apiErrorMessage(err, 'Payment verification error — contact support if you were charged.'))
        }
        resolve()
      },
    })
    rzp.on?.('payment.failed', (r) => onFailure(r?.error?.description || 'Payment failed — try again.'))
    rzp.open()
  })
}
